import { RefreshCw, Home, LogIn, AlertTriangle } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useRef } from 'react';

interface RouteErrorFallbackProps {
  error: Error;
  retry: () => void;
  reset: () => void;
  retryCount?: number;
}

// Custom hook to reset error boundary after navigation
function useResetErrorOnNavigation(reset: () => void) {
  const location = useLocation();
  const previousLocationKey = useRef<string | null>(null);

  useEffect(() => {
    // If this is not the first render and location has changed, reset error boundary
    if (previousLocationKey.current !== null && location.key !== previousLocationKey.current) {
      // Reset error boundary after navigation is complete
      const timeoutId = setTimeout(() => {
        reset();
      }, 0);

      return () => clearTimeout(timeoutId);
    }

    // Update previous location key
    previousLocationKey.current = location.key;
  }, [location.key, reset]);

  // For manual navigation from error fallback buttons
  const navigateAndReset = (navigateFn: () => void) => {
    navigateFn();
    // The useEffect above will handle the reset when location changes
  };

  return navigateAndReset;
}

// Generic page error fallback
export function PageErrorFallback({
  error,
  retry,
  reset,
  retryCount = 0,
}: RouteErrorFallbackProps) {
  const navigate = useNavigate();
  const canRetry = retryCount < 3;
  const navigateAndReset = useResetErrorOnNavigation(reset);

  return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center p-8">
      <div className="text-center max-w-lg mx-auto">
        <div className="mb-6">
          <div className="w-20 h-20 mx-auto bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
            <AlertTriangle className="w-10 h-10 text-red-600 dark:text-red-400" />
          </div>
        </div>

        <h1 className="text-2xl font-bold text-theme-light-text-primary dark:text-theme-dark-text-primary mb-4">
          Page Error
        </h1>
        <p className="text-theme-light-text-secondary dark:text-theme-dark-text-secondary mb-6">
          We encountered an error while loading this page. This might be a temporary issue.
        </p>

        {process.env.NODE_ENV === 'development' && (
          <details className="mb-6 text-left bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
            <summary className="cursor-pointer text-sm font-medium text-theme-light-text-secondary dark:text-theme-dark-text-secondary hover:text-theme-light-text-primary dark:hover:text-theme-dark-text-primary">
              Error Details (Development)
            </summary>
            <pre className="mt-3 text-xs text-theme-light-text-muted dark:text-theme-dark-text-muted overflow-auto max-h-40 bg-theme-light-bg dark:bg-theme-dark-bg p-3 rounded border border-gray-200 dark:border-gray-700">
              {error.stack}
            </pre>
          </details>
        )}

        <div className="flex flex-wrap gap-3 justify-center">
          {canRetry && (
            <button
              onClick={retry}
              className="inline-flex items-center px-4 py-2 bg-blue-600 dark:bg-blue-700 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-800 transition-colors"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Try Again {retryCount > 0 && `(${3 - retryCount} left)`}
            </button>
          )}

          <button
            onClick={() => navigateAndReset(() => navigate(-1))}
            className="inline-flex items-center px-4 py-2 bg-gray-600 dark:bg-gray-700 text-white rounded-lg hover:bg-gray-700 dark:hover:bg-gray-800 transition-colors"
          >
            Go Back
          </button>

          <button
            onClick={() => navigateAndReset(() => navigate('/overview'))}
            className="inline-flex items-center px-4 py-2 bg-gray-500 dark:bg-gray-600 text-white rounded-lg hover:bg-gray-600 dark:hover:bg-gray-700 transition-colors"
          >
            <Home className="w-4 h-4 mr-2" />
            Dashboard
          </button>
        </div>
      </div>
    </div>
  );
}

// Authentication error fallback
export function AuthErrorFallback({ retry, reset, retryCount = 0 }: RouteErrorFallbackProps) {
  const navigate = useNavigate();
  const canRetry = retryCount < 3;
  const navigateAndReset = useResetErrorOnNavigation(reset);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-8 bg-gray-50">
      <div className="text-center max-w-md mx-auto bg-white rounded-lg shadow-lg p-8">
        <div className="mb-6">
          <div className="w-16 h-16 mx-auto bg-red-100 rounded-full flex items-center justify-center">
            <LogIn className="w-8 h-8 text-red-600" />
          </div>
        </div>

        <h1 className="text-xl font-bold text-gray-900 mb-4">Authentication Error</h1>
        <p className="text-gray-600 mb-6">
          There was a problem with the authentication system. Please try logging in again.
        </p>

        <div className="flex flex-col gap-3">
          {canRetry && (
            <button
              onClick={retry}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Try Again
            </button>
          )}

          <button
            onClick={() => navigateAndReset(() => navigate('/login'))}
            className="w-full px-4 py-2 bg-[#E34290] text-white rounded-lg hover:bg-[#d63384] transition-colors"
          >
            Go to Login
          </button>
        </div>
      </div>
    </div>
  );
}

// Dashboard/protected route error fallback
export function DashboardErrorFallback({
  error,
  retry,
  reset,
  retryCount = 0,
}: RouteErrorFallbackProps) {
  const navigate = useNavigate();
  const canRetry = retryCount < 3;
  const navigateAndReset = useResetErrorOnNavigation(reset);

  return (
    <div className="p-8">
      <div className="max-w-2xl mx-auto text-center">
        <div className="mb-6">
          <div className="w-16 h-16 mx-auto bg-red-100 rounded-full flex items-center justify-center">
            <AlertTriangle className="w-8 h-8 text-red-600" />
          </div>
        </div>

        <h2 className="text-xl font-semibold text-gray-900 mb-4">Dashboard Error</h2>
        <p className="text-gray-600 mb-6">
          We're having trouble loading this part of the dashboard. Your data is safe.
        </p>

        {process.env.NODE_ENV === 'development' && (
          <details className="mb-6 text-left bg-gray-50 rounded-lg p-4">
            <summary className="cursor-pointer text-sm font-medium text-gray-700">
              Technical Details
            </summary>
            <pre className="mt-3 text-xs text-gray-600 overflow-auto max-h-32 bg-white p-3 rounded border">
              {error.message}
            </pre>
          </details>
        )}

        <div className="flex flex-wrap gap-3 justify-center">
          {canRetry && (
            <button
              onClick={retry}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Try Again
            </button>
          )}

          <button
            onClick={() => navigateAndReset(() => navigate('/overview'))}
            className="inline-flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            <Home className="w-4 h-4 mr-2" />
            Go to Overview
          </button>
        </div>
      </div>
    </div>
  );
}

// API/Data loading error fallback

export function ApiErrorFallback({ retry, retryCount = 0 }: RouteErrorFallbackProps) {
  const canRetry = retryCount < 3;

  return (
    <div className="p-6 bg-red-50 border border-red-200 rounded-lg">
      <div className="flex items-start">
        <div className="flex-shrink-0">
          <AlertTriangle className="w-5 h-5 text-red-400" />
        </div>
        <div className="ml-3 flex-1">
          <h3 className="text-sm font-medium text-red-800">Data Loading Error</h3>
          <div className="mt-2 text-sm text-red-700">
            <p>Unable to load data. This might be a temporary connectivity issue.</p>
          </div>
          {canRetry && (
            <div className="mt-4">
              <button
                onClick={retry}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-red-800 bg-red-100 hover:bg-red-200 transition-colors"
              >
                <RefreshCw className="w-4 h-4 mr-1" />
                Retry
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Compact error fallback for smaller components

export function CompactErrorFallback({ retry, retryCount = 0 }: RouteErrorFallbackProps) {
  const canRetry = retryCount < 3;

  return (
    <div className="p-4 text-center border-2 border-dashed border-gray-300 rounded-lg bg-gray-50">
      <AlertTriangle className="w-6 h-6 text-gray-400 mx-auto mb-2" />
      <p className="text-sm text-gray-600 mb-3">Something went wrong</p>
      {canRetry && (
        <button
          onClick={retry}
          className="text-xs px-3 py-1 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
        >
          Retry
        </button>
      )}
    </div>
  );
}
